import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { ArrowLeft, AlertTriangle, Save, Eye } from 'lucide-react';

const MaintenancePage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    block_register: false,
    block_login: false,
    message: '🔧 Sistema en Mantenimiento\n\nEstamos realizando mejoras en nuestro sistema.\nPor favor, intenta más tarde.\n\nDisculpa las molestias.'
  });
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/maintenance/config`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setConfig({
        enabled: response.data.enabled,
        block_register: response.data.block_register,
        block_login: response.data.block_login,
        message: response.data.message
      });
    } catch (err) {
      console.error('Error cargando configuración:', err);
      setError('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/maintenance/config`,
        config,
        { 
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          params: config
        }
      );
      setSuccess('✅ Configuración guardada exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error guardando:', err);
      setError('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const formatMessage = (text) => {
    return text.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < text.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <AlertTriangle className="text-orange-500" size={32} />
              Modo Mantenimiento
            </h1>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Controla el acceso al registro y login del sistema
            </p>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-lg">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-100 text-green-800 rounded-lg">
            {success}
          </div>
        )}

        {/* Main Card */}
        <div className={`rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg p-6`}>
          {/* Estado del Modo Mantenimiento */}
          <div className="mb-6">
            <label className="flex items-center space-x-3 cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                  className="sr-only"
                />
                <div className={`w-14 h-8 rounded-full transition ${
                  config.enabled ? 'bg-red-500' : isDark ? 'bg-gray-600' : 'bg-gray-300'
                }`}></div>
                <div className={`absolute left-1 top-1 w-6 h-6 bg-white rounded-full transition transform ${
                  config.enabled ? 'translate-x-6' : ''
                }`}></div>
              </div>
              <div>
                <span className="text-lg font-semibold">
                  {config.enabled ? '🔴 Modo Mantenimiento ACTIVO' : '🟢 Sistema Normal'}
                </span>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {config.enabled ? 'Los usuarios verán el mensaje de mantenimiento' : 'El sistema funciona normalmente'}
                </p>
              </div>
            </label>
          </div>

          <hr className={`my-6 ${isDark ? 'border-gray-700' : 'border-gray-200'}`} />

          {/* Opciones de Bloqueo */}
          <div className="space-y-4 mb-6">
            <h3 className="text-lg font-semibold mb-3">¿Qué deseas bloquear?</h3>
            
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={config.block_register}
                onChange={(e) => setConfig({ ...config, block_register: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="font-medium">Bloquear Registro</span>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Los usuarios no podrán crear nuevas cuentas
                </p>
              </div>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={config.block_login}
                onChange={(e) => setConfig({ ...config, block_login: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="font-medium">Bloquear Login</span>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Los usuarios no podrán iniciar sesión
                </p>
              </div>
            </label>
          </div>

          <hr className={`my-6 ${isDark ? 'border-gray-700' : 'border-gray-200'}`} />

          {/* Mensaje Personalizado */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">
              Mensaje de Mantenimiento
            </label>
            <textarea
              value={config.message}
              onChange={(e) => setConfig({ ...config, message: e.target.value })}
              className={`w-full px-4 py-3 rounded-lg border ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              rows="6"
              placeholder="Escribe el mensaje que verán los usuarios..."
            />
            <p className={`text-xs mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              💡 Puedes usar emojis y saltos de línea para formato básico
            </p>
          </div>

          {/* Botones de Acción */}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
            >
              <Save size={20} />
              {saving ? 'Guardando...' : 'Guardar Configuración'}
            </button>

            <button
              onClick={() => setShowPreview(!showPreview)}
              className={`px-6 py-3 rounded-lg flex items-center gap-2 ${
                isDark 
                  ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
              }`}
            >
              <Eye size={20} />
              Preview
            </button>
          </div>
        </div>

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
              <button
                onClick={() => setShowPreview(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
              <div className="text-center">
                <div className="text-4xl mb-4">⚠️</div>
                <div className="text-gray-900 whitespace-pre-wrap text-lg leading-relaxed">
                  {formatMessage(config.message)}
                </div>
                <button
                  onClick={() => setShowPreview(false)}
                  className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaintenancePage;
